import { subDays } from 'date-fns';
import { observer } from 'mobx-react';
import React, { PropsWithChildren, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';

import { Button, CircularProgress, NoSsr } from '@mui/material';

import useLoginDialog from 'hooks/use-login-dialog';
import useStores from 'hooks/use-stores';
import { useConfigs } from 'modules/configs';
import { useHistoryOfChanges } from 'pages/Bulletin/providers/history-of-changes';
import { ChartData } from 'pages/Bulletin/types/chart-data';
import { HistoryOfChangesItem } from 'pages/Bulletin/types/history-of-changes';
import isEmptyRecursive from 'utils/is-empty-recursive';

import useStyles from './styles';

import LineChart from '../LineChart';

interface Props {
    scale?: number;
    getChart: (data: HistoryOfChangesItem[]) => ChartData | null;
}

const CHART_WIDTH = 250;
const CHART_PADDING = 0;

const Statistics: React.FC<PropsWithChildren<Props>> = ({ getChart, scale }) => {
    const { classes } = useStyles();
    const { userStore } = useStores();
    const location = useLocation();
    const [dialog, openDialog] = useLoginDialog(location.pathname + location.search);
    const { settings } = useConfigs();

    const { data, isLoading, load } = useHistoryOfChanges();
    useEffect(() => {
        load();
    }, [load]);

    const chart = useMemo(() => {
        if (!userStore.userLoaded) return [];
        if (!userStore.isAuthorized && settings.limitDataEnabled) {
            return Array.from({ length: 10 }, (_, index) => ({
                score: Math.random(),
                version: index + 1,
                published: subDays(Date.now(), index)
            }));
        }
        return getChart(data.slice().reverse()) ?? [];
    }, [data, getChart, settings.limitDataEnabled, userStore.isAuthorized, userStore.userLoaded]);

    if (!userStore.userLoaded) {
        return null;
    }
    if (isLoading) {
        return (
            <div className={classes.spinner}>
                <CircularProgress size={24} />
            </div>
        );
    }
    if (isEmptyRecursive(chart)) {
        return null;
    }

    return (
        <>
            <div className={classes.content} style={{ minWidth: CHART_WIDTH, maxWidth: CHART_WIDTH }}>
                <div className={classes.chart}>
                    <NoSsr>
                        <LineChart width={CHART_WIDTH} padding={CHART_PADDING} chart={chart} scale={scale} />
                        {!userStore.isAuthorized && settings.limitDataEnabled ? (
                            <div className={classes.blur}>
                                <Button
                                    className={classes.signIn}
                                    onClick={openDialog}
                                    centerRipple
                                    variant="contained"
                                    color="inherit"
                                >
                                    {'Sign in to unlock'}
                                </Button>
                            </div>
                        ) : null}
                    </NoSsr>
                </div>
            </div>
            {dialog}
        </>
    );
};

export default observer(Statistics);
